import { useMemo } from "react";

import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { creditCardVerification } from "@/hooks/firestore/models/creditCardVerification";
import { useCreditCards } from "@/hooks/firestore/user/useCreditCards";

const allowedStatuses = [
  "UnderThreshold",
  "Pending",
  "Failed",
  "InProgress",
  "Complete",
  "ManuallyVerified",
  "ThreeDsVerified",
];

const useCreditCardVerifications = () => {
  const {
    data: verifications,
    error,
    isLoading,
  } = useFirestore(
    "/users/{userId}/creditCardVerifications",
    creditCardVerification.array(),
    { keyPath: "id" },
  );

  const { creditCards } = useCreditCards();

  const sortedVerifications = useMemo(() => {
    if (!verifications) return [];
    const enriched = verifications.map((v) => {
      const card = creditCards.find(
        (c) => c.numberFirst === v.numberFirst && c.numberLast === v.numberLast,
      );

      return {
        ...v,
        expiryMonth: card?.expiryMonth,
        expiryYear: card?.expiryYear,
        expired: card?.expired,
      };
    });

    return enriched
      .filter((verification) => allowedStatuses.includes(verification.status))
      .sort((a, b) => {
        const aOrder = allowedStatuses.indexOf(a.status);
        const bOrder = allowedStatuses.indexOf(b.status);
        return aOrder - bOrder;
      });
  }, [verifications, creditCards]);

  return {
    verifications: sortedVerifications,
    error,
    isLoading,
  };
};

type CreditCardVerification = Exclude<
  ReturnType<typeof useCreditCardVerifications>["verifications"],
  undefined
>;

export { useCreditCardVerifications, type CreditCardVerification };
